import { memo, useState } from "react";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import dayjs from "dayjs";
import EntryRow, { ENTRY_ROW_COLUMNS } from "app/components/reports/bulk/EntryRow";
import RowDatePicker from "app/components/reports/bulk/RowDatePicker";
import { addRowAfter, duplicateRow, removeRowAt, dayRollUp } from "utils/bulkActivityUtil";
import { getLatestActivityDate, getEarliestActivityDate } from "utils/dateUtil";
import * as AppConstants from "AppConstants";

const HEADERS = [
  { label: "Day", required: true },
  { label: "Task ticket" },
  { label: "Description" },
  { label: "Activity type", required: true },
  { label: "Status" },
  { label: "Hours", required: true, align: "center" },
  { label: "Row", align: "center" },
];

/**
 * The bulk entry table: one row per activity, plus a chip per distinct date showing what that day
 * will hold once saved.
 *
 * Rows are a controlled list — every mutation goes out through onRowsChange, so the dialog stays the
 * single owner of the data and validation runs in one place.
 *
 * @since NEXT_VERSION
 */
const EntryRowsTable = ({
  rows,
  rowErrors = [],
  activityTypes = [],
  savedHoursByDate = {},
  savedHoursLoading = false,
  // The oldest day a row may take. The dialog owns it because editing relaxes it to the date the
  // activity already carries. @since NEXT_VERSION
  minDate = dayjs(getEarliestActivityDate()),
  onRowsChange,
  onVisibleMonthChange = () => {},
}) => {
  const [picker, setPicker] = useState({ index: -1, anchorEl: null, month: dayjs() });

  const patchRow = (index, patch) => {
    const next = rows.slice();
    next[index] = { ...next[index], ...patch };
    onRowsChange(next);
  };

  // The dialog fetches saved hours a month at a time, so it has to know which month is on screen —
  // otherwise paging the picker shows a month with no badges, which is exactly when the user is
  // deciding where to put the entry.
  const openPicker = (index, anchorEl) => {
    const current = rows[index]?.date;
    const month = current ? dayjs(current) : dayjs();
    setPicker({ index, anchorEl, month });
    onVisibleMonthChange(month);
  };

  const closePicker = () => setPicker((prev) => ({ ...prev, index: -1, anchorEl: null }));

  const pickDate = (iso) => {
    patchRow(picker.index, { date: iso });
    closePicker();
  };

  const rollUp = dayRollUp(rows, savedHoursByDate);

  return (
    <MDBox>
      <MDBox
        sx={{
          border: "1px solid var(--border)",
          borderRadius: "8px",
          backgroundColor: "var(--surface)",
          // Below the minWidth below, the seven columns stop fitting; scrolling the table keeps
          // every field usable rather than crushing Description.
          overflowX: "auto",
        }}
      >
        <MDBox sx={{ minWidth: 970 }}>
          <MDBox
            sx={{
              display: "grid",
              gridTemplateColumns: ENTRY_ROW_COLUMNS,
              gap: 1,
              alignItems: "center",
              px: 1.75,
              py: 1.25,
              backgroundColor: "var(--surface-2)",
              borderBottom: "1px solid var(--border-strong)",
              borderRadius: "8px 8px 0 0",
            }}
          >
            {HEADERS.map((header) => (
              <MDTypography
                key={header.label}
                variant="caption"
                fontWeight="bold"
                color="secondary"
                sx={{ textTransform: "uppercase", textAlign: header.align ?? "left" }}
              >
                {header.label}
                {header.required && (
                  <MDBox component="span" sx={{ color: "var(--error)" }}>
                    {" *"}
                  </MDBox>
                )}
              </MDTypography>
            ))}
          </MDBox>

          {rows.map((row, index) => (
            <EntryRow
              key={row.id}
              row={row}
              index={index}
              errors={rowErrors[index] ?? {}}
              activityTypes={activityTypes}
              savedHours={Number(savedHoursByDate[row.date]) || 0}
              isOnlyRow={rows.length <= 1}
              canAddRows={rows.length < AppConstants.MAX_ACTIVITY_ROWS_PER_DIALOG}
              onChange={patchRow}
              onAdd={(at) => onRowsChange(addRowAfter(rows, at))}
              onDuplicate={(at) => onRowsChange(duplicateRow(rows, at))}
              onRemove={(at) => onRowsChange(removeRowAt(rows, at))}
              onOpenPicker={openPicker}
            />
          ))}
        </MDBox>
      </MDBox>

      <MDBox sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.75, flexWrap: "wrap" }}>
        <MDTypography
          variant="caption"
          fontWeight="bold"
          color="secondary"
          mr={0.5}
          sx={{ textTransform: "uppercase" }}
        >
          Day totals
        </MDTypography>
        {rollUp.map((day) => (
          <MDBox
            key={day.date}
            sx={{
              px: 1.25,
              py: 0.5,
              borderRadius: "999px",
              fontSize: 12,
              fontWeight: 600,
              // Held back while the saved figures are in flight: the total is short until they
              // land, so a day that is only over the cap once they arrive would flash twice.
              backgroundColor: !savedHoursLoading && day.overCap ? "var(--error-soft-2)" : "var(--surface-3)",
              color: !savedHoursLoading && day.overCap ? "var(--error-strong)" : "var(--text-2)",
            }}
          >
            {`${dayjs(day.date).format("ddd DD MMM")} · ${day.totalHours} h` +
              (day.savedHours > 0 ? ` (${day.savedHours} h already saved)` : "")}
          </MDBox>
        ))}
      </MDBox>

      <RowDatePicker
        open={picker.index >= 0}
        anchorEl={picker.anchorEl}
        month={picker.month}
        value={picker.index >= 0 ? rows[picker.index]?.date : null}
        savedHoursByDate={savedHoursByDate}
        datesUsedByOtherRows={rows
          .filter((unused, index) => index !== picker.index)
          .map((other) => other.date)
          .filter(Boolean)}
        minDate={minDate}
        maxDate={dayjs(getLatestActivityDate())}
        onPick={pickDate}
        onClose={closePicker}
        onMonthChange={(month) => {
          setPicker((prev) => ({ ...prev, month }));
          onVisibleMonthChange(month);
        }}
      />
    </MDBox>
  );
};

export default memo(EntryRowsTable);
